import { forwardRef } from 'react';
import { cn } from './cn';

export type DividerOrientation = 'horizontal' | 'vertical';

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Line direction. Horizontal spans full width; vertical stretches to parent height. */
  orientation?: DividerOrientation;
  /** Optional centered label (horizontal only) rendered between two line segments. */
  children?: React.ReactNode;
}

/**
 * Visual separator — replaces MUI <Divider />.
 * Horizontal: full-width hairline; with children, a centered label flanked by lines.
 * Vertical: a self-stretching 1px line for use inside flex rows.
 */
export const Divider = forwardRef<HTMLDivElement, DividerProps>(
  ({ orientation = 'horizontal', className, children, ...props }, ref) => {
    if (orientation === 'vertical') {
      return (
        <div
          ref={ref}
          role="separator"
          aria-orientation="vertical"
          className={cn('inline-block w-px self-stretch border-l border-line', className)}
          {...props}
        />
      );
    }

    if (children != null && children !== false) {
      return (
        <div
          ref={ref}
          role="separator"
          aria-orientation="horizontal"
          className={cn('flex w-full items-center gap-3', className)}
          {...props}
        >
          <span aria-hidden className="h-px flex-1 border-t border-line" />
          <span className="shrink-0 text-xs font-medium text-muted">{children}</span>
          <span aria-hidden className="h-px flex-1 border-t border-line" />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation="horizontal"
        className={cn('w-full border-t border-line', className)}
        {...props}
      />
    );
  },
);

Divider.displayName = 'Divider';
